import { ReactNode } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, LucideIcon } from "lucide-react";

interface Props {
  id: string;
  number: string;
  eyebrow: string;
  title: string;
  intro: string;
  icon: LucideIcon;
  ctaLabel: string;
  ctaHref: string;
  tone?: "default" | "alt";
  children: ReactNode;
}

export default function StageSection({
  id,
  number,
  eyebrow,
  title,
  intro,
  icon: Icon,
  ctaLabel,
  ctaHref,
  tone = "default",
  children,
}: Props) {
  return (
    <section
      id={id}
      className={`scroll-mt-32 py-16 md:py-24 ${tone === "alt" ? "bg-card/30" : "bg-background"}`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="lg:sticky lg:top-28"
          >
            <div className="flex items-center gap-3 mb-5">
              <span aria-hidden="true" className="font-display text-5xl md:text-6xl font-bold text-primary leading-none">
                {number}
              </span>
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </div>
            </div>
            <div className="text-primary text-xs font-semibold tracking-[0.2em] uppercase mb-3">
              {eyebrow}
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4">
              {title}
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              {intro}
            </p>
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </section>
  );
}